import { useLayoutEffect, useRef, useState } from 'react';

const MARGEM_SEGURANCA_PX = 8;

export type PosicaoDropdown = {
    abrirParaCima: boolean;
    abrirParaEsquerda: boolean;
};

export type OpcoesDropdownEncaixarNaTela = {
    // 'esquerda' (padrão): menu nasce ancorado à direita do botão (right:0) e cresce para a
    // esquerda — o problema a detectar é o menu ficar cortado na borda ESQUERDA da tela.
    // 'direita': menu nasce ao lado direito do botão (left:100%) e cresce para a direita —
    // o problema a detectar é o menu ficar cortado na borda DIREITA da tela.
    ladoPreferido?: 'esquerda' | 'direita';
};

// Sobe a árvore de ancestrais até achar um container com scroll (overflow-x/y auto|scroll) — é
// o limite que realmente recorta visualmente o menu quando ele é posicionado dentro de uma
// tabela/grid rolável (ex: GridItensPesquisaProdutos_Div_Scroll com overflow-x:scroll, que por
// spec já força overflow-y:auto). Sem esse ancestral, o limite cai de volta para a janela inteira.
const encontrarAncestralComScroll = (elemento: HTMLElement | null): HTMLElement | null => {
    let atual = elemento?.parentElement ?? null;
    while (atual && atual !== document.body) {
        const estilo = window.getComputedStyle(atual);
        if (/(auto|scroll)/.test(estilo.overflowY) || /(auto|scroll)/.test(estilo.overflowX)) {
            return atual;
        }
        atual = atual.parentElement;
    }
    return null;
};

// Mede o menu já renderizado na posição padrão (definida pelo CSS do chamador) e decide se ele
// precisa abrir para cima/para o lado oposto para não ficar cortado pela borda do container
// rolável mais próximo (ou da janela, se não houver um).
// useLayoutEffect roda antes do paint: a correção de posição não gera flicker visível.
const useDropdownEncaixarNaTela = <T extends HTMLElement>(aberto: boolean, opcoes?: OpcoesDropdownEncaixarNaTela) => {
    const ladoPreferido = opcoes?.ladoPreferido ?? 'esquerda';
    const menuRef = useRef<T>(null);
    const [posicao, setPosicao] = useState<PosicaoDropdown>({ abrirParaCima: false, abrirParaEsquerda: false });

    useLayoutEffect(() => {
        if (!aberto) {
            setPosicao({ abrirParaCima: false, abrirParaEsquerda: false });
            return;
        }

        const rect = menuRef.current?.getBoundingClientRect();
        if (!rect) return;

        const rectContainer = encontrarAncestralComScroll(menuRef.current)?.getBoundingClientRect();
        const limiteInferior = rectContainer?.bottom ?? window.innerHeight;
        const limiteDireito = rectContainer?.right ?? window.innerWidth;
        const limiteEsquerdo = rectContainer?.left ?? 0;

        setPosicao({
            abrirParaCima: rect.bottom + MARGEM_SEGURANCA_PX > limiteInferior,
            abrirParaEsquerda: ladoPreferido === 'direita'
                ? rect.right + MARGEM_SEGURANCA_PX > limiteDireito
                : rect.left < limiteEsquerdo + MARGEM_SEGURANCA_PX,
        });
    }, [aberto, ladoPreferido]);

    return { menuRef, ...posicao };
};

export default useDropdownEncaixarNaTela;
